import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Card, 
  CardHeader, 
  CardContent, 
  Typography,
  Button,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  styled,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Error as ErrorIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Terminal as TerminalIcon,
  GitHub as GitHubIcon,
  Launch as ExternalLinkIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { BglOverlayLoader } from 'components/loading';
import { useGlobalConfig } from 'context/GlobalContext';
import { useLocation } from 'react-router';
import { ROUTE_TREE } from 'routes/AppRoutes';
import { findEnvironmentByProcessId } from 'framework/utility/EnvironmentUtils';
import { useDispatch, useSelector } from 'react-redux';
import { selectPilotList, updatePilotList, selectPilot } from 'store/dataReducers/pilotsSlice';
// import { MOCK_ENVIRONMENT_CREATION_PROCESS } from 'constants/environment-creation-data';
import type { ProcessStep, RecentLog } from 'types/common/models/environment-creation';
import type { EnvironmentCreationProcess } from 'types/common/models/environment-creation';
import { PilotApis } from 'apis/pilotAPI';

// Define timestamp keys that need UTC to local time conversion
const TIMESTAMP_KEYS = new Set([
  'created_at',
  'updated_at', 
  'started_at',
  'completed_at',
  'timestamp'
]);

// Utility function to convert UTC timestamps to user time
const convertUtcToUserTime = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(convertUtcToUserTime);
  }
  
  const converted = { ...obj };
  
  for (const [key, value] of Object.entries(converted)) {
    if (TIMESTAMP_KEYS.has(key) && typeof value === 'string') {
      try {
        // Parse the UTC timestamp and convert to user's local time
        const d = new Date(value + "Z");
        d.toLocaleString(undefined, {
          year: "numeric",
          month: "short",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          timeZoneName: "short"
        })
        converted[key] = d;
      } catch (error) {
        console.warn(`Failed to convert timestamp for key ${key}:`, error);
        // Keep original value if conversion fails
      }
    } else if (typeof value === 'object' && value !== null) {
      // Recursively convert nested objects
      converted[key] = convertUtcToUserTime(value);
    }
  }
  
  return converted;
};

// Styled components
const StatusIcon = ({ status }: { status: ProcessStep['status'] }) => {
  switch (status) {
    case 'completed': 
      return <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />;
    case 'in_progress': 
      return <ScheduleIcon sx={{ color: 'primary.main', fontSize: 20, animation: 'spin 2s linear infinite' }} />;
    case 'failed': 
      return <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />;
    default: 
      return <InfoIcon sx={{ color: 'text.secondary', fontSize: 20 }} />;
  }
};

const LogLevelChip = ({ level }: { level: RecentLog['log_level'] }) => {
  const getColor = () => {
    switch (level) {
      case 'ERROR': return 'error';
      case 'WARN': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Chip 
      label={level} 
      size="small" 
      color={getColor()}
      variant={level === 'INFO' ? 'outlined' : 'filled'}
      sx={{ fontSize: '0.7rem', height: 20 }}
    />
  );
};

const ScrollableLogsContainer = styled(Box)(({ theme }) => ({
  maxHeight: 400,
  overflow: 'auto',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  fontFamily: 'monospace',
  fontSize: '0.75rem',
  '&::-webkit-scrollbar': {
    width: 8,
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.background.default,
    borderRadius: 4,
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.divider,
    borderRadius: 4,
  },
}));

export const EnvironmentCreationStatusPage = () => {
  const { rc } = useGlobalConfig();
  const dispatch = useDispatch();
  const pilotList = useSelector(selectPilotList);
  const [data, setData] = useState<EnvironmentCreationProcess | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const processId = new URLSearchParams(location.search).get('processId');
  const projectId = new URLSearchParams(location.search).get('projectId');

  // Refresh pilot list and sync project context on component mount
  useEffect(() => {
    const refreshPilotListAndSyncContext = async () => {
      try {
        const response = await PilotApis.getPilots(rc);
        if (response.data?.data) {
          dispatch(updatePilotList(response.data.data));
          
          // Sync project context after pilot list is loaded
          if (projectId) {
            const matchingPilot = response.data.data.find((project:any) => project.projectId === projectId);
            if (matchingPilot) {
              dispatch(selectPilot({ 
                projectId: matchingPilot.projectId, 
                docId: matchingPilot.documentId || '' 
              }));
              console.log('Synced project context for projectId:', projectId);
            }
          }
        }
      } catch (error) {
        console.error('Error refreshing pilot list:', error);
      }
    };
    
    refreshPilotListAndSyncContext();
  }, [rc, dispatch, projectId]);

  // Fallback: Sync project context if pilot list is already loaded and projectId changes
  useEffect(() => {
    if (projectId && pilotList.length > 0) {
      const matchingPilot = pilotList.find(pilot => pilot.projectId === projectId);
      if (matchingPilot) {
        dispatch(selectPilot({ 
          projectId: matchingPilot.projectId, 
          docId: matchingPilot.documentId || '' 
        }));
        console.log('Fallback: Synced project context for projectId:', projectId);
      }
    }
  }, [projectId, pilotList, dispatch]);

  // Function to update project environment status using PATCH API
  const updateProjectEnvironmentStatus = async (newStatus: string) => {
    if (!data?.project || !processId) return;

    try {
      // Find the matching pilot and environment
      const matchingPilot = (pilotList as any[]).find(pilot => 
        pilot.name === data.project || pilot.projectId === data.project
      );

      if (!matchingPilot) {
        console.error('No matching pilot found for project:', data.project);
        return;
      }

            // Find the environment with matching process_id
      const matchingEnvironment = findEnvironmentByProcessId(matchingPilot, processId);
      
      if (!matchingEnvironment) {
        console.error('No matching environment found for process_id:', processId);
        return;
      }

      // Check if status is already the same
      if (matchingEnvironment.status === newStatus) {
        console.log('Environment status is already', newStatus, '- no update needed');
        return;
      }

      // Call the PATCH API to update environment status using the environment ID
      const response = await PilotApis.updateProjectEnvironmentStatus(
        rc, 
        matchingPilot.projectId, 
        matchingEnvironment.id, // Use the environment ID, not process_id
        newStatus
      );

      if (response.error) {
        console.error('Error updating project environment status:', response.error);
        return;
      }

      // Update Redux store with the returned updated project data
      if (response.data) {
        const updatedPilotList = (pilotList as any[]).map(pilot => {
          if (pilot.name === data.project || pilot.projectId === data.project) {
            return response.data; // Use the updated project data from API
          }
          return pilot;
        });

        dispatch(updatePilotList(updatedPilotList));
        console.log('Project environment status updated successfully from', matchingEnvironment.status, 'to:', newStatus);
      } else {
        // Fallback: manually update the Redux store if API doesn't return updated data
        const updatedPilotList = (pilotList as any[]).map(pilot => {
          if (pilot.name === data.project || pilot.projectId === data.project) {
            const updatedProjectEnvironments = (pilot.projectEnvironments as any[])?.map((env: any) => {
              if (env.process_id === processId) {
                return {
                  ...env,
                  status: newStatus
                };
              }
              return env;
            }) || [];

            return {
              ...pilot,
              projectEnvironments: updatedProjectEnvironments
            };
          }
          return pilot;
        });

        dispatch(updatePilotList(updatedPilotList));
        console.log('Project environment status updated using fallback method');
      }

    } catch (error) {
      console.error('Error updating project environment status:', error);
    }
  };

  useEffect(() => {
    if (!processId) {
      rc.uiRouter.navigate(ROUTE_TREE.PROJECT.DETAILS);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await PilotApis.checkEnvironmentProcessStatus(rc, projectId || '', processId);
        if (response.data) {
          // Convert UTC timestamps to user's local time
          const convertedData = convertUtcToUserTime(response.data);
          setData(convertedData);
          await updateProjectEnvironmentStatus(convertedData.status);
        }
      } catch (error) {
        console.error('Error fetching process status:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();

    // Set up periodic refresh for in-progress processes
    let intervalId: NodeJS.Timeout | null = null;
    if (data?.status === 'in_progress') {
      intervalId = setInterval(async () => {
        try {
          const response = await PilotApis.checkEnvironmentProcessStatus(rc, projectId || '', processId);
          if (response.data) {
            // Convert UTC timestamps to user's local time
            const convertedData = convertUtcToUserTime(response.data);
            setData(convertedData);
            await updateProjectEnvironmentStatus(convertedData.status);
            
            // Stop refreshing if process is completed or failed
            if (convertedData.status === 'completed' || convertedData.status === 'failed') {
              if (intervalId) {
                clearInterval(intervalId);
              }
            }
          }
        } catch (error) {
          console.error('Error in periodic status refresh:', error);
        }
      }, 60000); // Refresh every 60 seconds
    }

    // Cleanup interval on unmount or when data changes
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [rc, processId, dispatch, data?.status]); // Added data?.status to dependency array

  // Cleanup effect to ensure Redux store is updated when component unmounts
  useEffect(() => {
    return () => {
      // If we have data and the process is completed or failed, ensure Redux store is updated
      if (data && (data.status === 'completed' || data.status === 'failed')) {
        updateProjectEnvironmentStatus(data.status);
      }
    };
  }, [data?.status]);

  const getDuration = (start: string | null, end: string | null): string => {
    if (!start || !end) return "N/A";
    try {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const diffMs = endDate.getTime() - startDate.getTime();
      const diffSeconds = Math.floor(diffMs / 1000);
      return `${diffSeconds}s`;
    } catch {
      return "N/A";
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const formatTimeAgo = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMinutes = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffMinutes > 0) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
      return 'Just now';
    } catch {
      return 'Unknown';
    }
  };

  const handleBackClick = async () => {
    // Refresh pilot list and sync context before navigating back to ensure latest status is shown
    try {
      const response = await PilotApis.getPilots(rc);
      if (response.data?.data) {
        dispatch(updatePilotList(response.data.data));
        
        // Sync project context after pilot list is refreshed
        if (projectId) {
          const matchingPilot = response.data.data.find((project:any) => project.projectId === projectId);
          if (matchingPilot) {
            dispatch(selectPilot({ 
              projectId: matchingPilot.projectId, 
              docId: matchingPilot.documentId || '' 
            }));
            console.log('Synced project context before navigation for projectId:', projectId);
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing pilot list before navigation:', error);
    }
    
    rc.uiRouter.navigate(ROUTE_TREE.PROJECT.DETAILS);
  };

  const handleRefreshStatus = async () => {
    if (!processId) return;
    
    setIsLoading(true);
    try {
      const response = await PilotApis.checkEnvironmentProcessStatus(rc, projectId || '', processId);
      if (response.data) {
        // Convert UTC timestamps to user's local time
        const convertedData = convertUtcToUserTime(response.data);
        setData(convertedData);
        await updateProjectEnvironmentStatus(convertedData.status);
      } else if (response.error) {
        console.error('Error refreshing process status:', response.error);
      }
    } catch (error) {
      console.error('Error refreshing process status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: { xs: 1, md: 4 }, bgcolor: 'background.default', minHeight: '100vh', position: 'relative' }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Environment Creation Status
        </Typography>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBack />}
          onClick={handleBackClick}
        >
          Back to Project Details
        </Button>
      </Box>

      {isLoading ? (
        null
      ) : !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <Typography variant="h6" color="text.secondary">
            No process data available
          </Typography>
        </Box>
      ) : (
        <>
          {/* Process Overview Card */}
          <Card sx={{ boxShadow: 1, borderRadius: 2, bgcolor: 'background.paper' }}>
            <CardHeader
              title={<Typography variant="h5" component="h2" sx={{ fontWeight: 600, textAlign: 'left', color: 'text.primary' }}>
                {data.process_name}
              </Typography>}
              subheader={<Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                {data.process_description}
              </Typography>}
            />
            <CardContent sx={{ pt: 0 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 3, mb: 3 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Project</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>{data.project}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Owner</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>{data.owner}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Started</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>{data.started_at?formatDate(data.started_at):formatDate(data.created_at)}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Last Updated</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>{formatTimeAgo(data.updated_at)}</Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, textAlign: 'left', color: 'text.primary' }}>
                  Overall Progress ({data.progress_percentage}%)
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={data.progress_percentage} 
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            </CardContent>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
              <Button 
                variant="outlined" 
                size="small"
                startIcon={<RefreshIcon />}
                onClick={handleRefreshStatus}
                disabled={isLoading}
              >
                Refresh Status
              </Button>
            </Box>
          </Card>

          {/* Creation Steps Card */}
          <Card sx={{ boxShadow: 1, borderRadius: 2, bgcolor: 'background.paper' }}>
            <CardHeader
              title={<Typography variant="h6" component="h3" sx={{ fontWeight: 600, textAlign: 'left', color: 'text.primary' }}>
                Creation Steps
              </Typography>}
              subheader={<Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                Detailed status of each step in the environment creation process.
              </Typography>}
            />
            <CardContent sx={{ pt: 0 }}>
              <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 60, textAlign: 'center', bgcolor: 'background.paper', color: 'text.primary' }}>Status</TableCell>
                      <TableCell sx={{ bgcolor: 'background.paper', color: 'text.primary' }}>Step Name</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, bgcolor: 'background.paper', color: 'text.secondary' }}>Description</TableCell>
                      <TableCell sx={{ width: 120, textAlign: 'center', bgcolor: 'background.paper', color: 'text.secondary' }}>Duration</TableCell>
                      <TableCell sx={{ width: 100, textAlign: 'right', bgcolor: 'background.paper', color: 'text.primary' }}>Logs</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.process_steps.map((step, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ textAlign: 'center', bgcolor: 'background.paper' }}>
                          <StatusIcon status={step.status} />
                        </TableCell>
                        <TableCell sx={{ bgcolor: 'background.paper' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {step.step_name}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, bgcolor: 'background.paper', color: 'text.secondary' }}>
                          {step.step_description}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', fontSize: '0.75rem', bgcolor: 'background.paper', color: 'text.secondary' }}>
                          {step.step_metadata?.duration_seconds 
                            ? `${step.step_metadata.duration_seconds}s` 
                            : getDuration(step.started_at, step.completed_at)
                          }
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', bgcolor: 'background.paper' }}>
                          {step.external_run_url ? (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={step.external_system === 'github' ? <GitHubIcon /> : <ExternalLinkIcon />}
                              href={step.external_run_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View
                            </Button>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              N/A
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Recent Logs Card */}
          <Card sx={{ boxShadow: 1, borderRadius: 2, bgcolor: 'background.paper' }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TerminalIcon sx={{ color: 'primary.main' }} />
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 600, textAlign: 'left', color: 'text.primary' }}>
                    Recent Logs
                  </Typography>
                </Box>
              }
              subheader={<Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                A stream of the most recent log entries for this process.
              </Typography>}
            />
            <CardContent sx={{ pt: 0 }}>
              <ScrollableLogsContainer>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {data.recent_logs.map((log, index) => (
                    <Box key={index} sx={{
                      display: 'grid',
                      gridTemplateColumns: '220px 1fr',
                      alignItems: 'center',
                      gap: 2,
                      width: '100%',
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80, flexShrink: 0, textAlign: 'left' }}>
                          {new Date(log.timestamp).toLocaleTimeString('en-US', { 
                            hour12: false, 
                            hour: '2-digit', 
                            minute: '2-digit', 
                            second: '2-digit',
                            fractionalSecondDigits: 3
                          })}
                        </Typography>
                        <LogLevelChip level={log.log_level} />
                      </Box>
                      <Typography variant="caption" sx={{ color: 'text.primary', textAlign: 'left', pl: 1 }}>
                        {log.message}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </ScrollableLogsContainer>
            </CardContent>
          </Card>
        </>
      )}

      {/* Overlay Loader for Operations */}
      <BglOverlayLoader
        open={isLoading}
        message="Processing..."
        size={50}
      />
    </Box>
  );
}; 